import * as api from '@gitbook/api-client';

const INTEGRATION_CATEGORIES = new Set(Object.values(api.IntegrationCategory));

/**
 * Assert that an integration site installation is an extended site installation.
 */
export function assertIsExtendedSiteInstallation(
    installation: api.IntegrationSiteInstallation
): asserts installation is Omit<api.IntegrationSiteInstallation, 'site'> & { site: api.Site } {
    if (!('site' in installation)) {
        throw new Error(
            `assertIsExtendedSiteInstallation: site is not defined: (${JSON.stringify(installation)})`
        );
    }

    if (typeof installation.site === 'string') {
        throw new Error(
            `Integration site installation does not have the site object, found ${typeof installation.site} ${installation.site} (space: ${'space' in installation ? typeof installation.space : 'undefined'})`
        );
    }
}

/**
 * Assert that an integration space installation is an extended space installation.
 */
export function assertIsExtendedSpaceInstallation(
    installation: api.IntegrationSpaceInstallation
): asserts installation is Omit<api.IntegrationSpaceInstallation, 'space'> & { space: api.Space } {
    if (!('space' in installation)) {
        throw new Error(
            `assertIsExtendedSpaceInstallation: space is not defined: (${JSON.stringify(installation)})`
        );
    }

    if (typeof installation.space === 'string') {
        throw new Error(
            `Integration space installation does not have the space object, found ${typeof installation.space} ${installation.space} (site: ${'site' in installation ? typeof installation.site : 'undefined'})`
        );
    }
}

/**
 * Check whether the integration has defined a configuration at any level
 */
export function integrationNeedsConfiguration(integration: api.Integration) {
    return Object.keys(integration.configurations ?? {}).length > 0;
}

/** Return the category used to group an integration in the UI. */
export function getPrimaryIntegrationCategory(
    integration: Pick<api.Integration, 'categories'>
): api.IntegrationCategory {
    const category = integration.categories?.[0];
    return category && INTEGRATION_CATEGORIES.has(category)
        ? category
        : api.IntegrationCategory.Other;
}
